import { Fragment, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { useProfilePhoto } from "../../hooks/useProfilePhoto";
import { useLogout } from "../../hooks/useLogout";
import { getActiveMsalInstance } from "../../authConfig";
import placeholderProfile from "../../assets/User-Picture.png";
import { SettingsDialog } from "../SettingsDialog";

const LogoutConfirmDialog = ({ open, onConfirm, onCancel, email }: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    email: string;
}) => {
    const cancelButtonRef = useRef(null);

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={onCancel}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm rounded-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 sm:mt-0 text-left">
                                            <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Are you sure you want to log out?
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">Log out of ChatJTC as {email}?</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                                    <a
                                        className="bg-jtc-pink hover:bg-jtc-purple flex items-center justify-center h-[40px] px-[17px] rounded-button text-jtc-white cursor-pointer"
                                        onClick={onConfirm}
                                    >
                                        <div className="font-black tracking-[0.15rem] text-[.75rem] uppercase">Log out</div>
                                    </a>
                                    <a
                                        className="bg-jtc-white border-2 border-jtc-purple hover:bg-jtc-grey flex items-center justify-center h-[40px] px-[17px] rounded-button text-jtc-purple cursor-pointer"
                                        ref={cancelButtonRef}
                                        onClick={onCancel}
                                    >
                                        <div className="font-black tracking-[0.15rem] text-[.75rem] uppercase">Cancel</div>
                                    </a>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export const UserButton = () => {
    const instance = getActiveMsalInstance();
    const activeAccount = instance?.getActiveAccount() ?? null;
    const profileUrl = useProfilePhoto() ?? placeholderProfile;
    const logout = useLogout();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const userText = `${activeAccount?.name}`;
    const email = activeAccount?.username ?? "";

    return (
        <>
            <div className="flyout-container inline-block relative">
                <div className="flyout-menu hidden absolute bottom-full left-[-.5rem] z-[100] md:w-[264px] w-[284.2px] transition-all ease-linear duration-300 bg-jtc-white rounded-lg shadow-lg-up">
                    <div className="py-2 px-5">
                        <div className="flex flex-col">
                            <div className="py-2 font-normal leading-7">
                                <a
                                    className="gap-1 w-fit ease-linear transition-transform duration-300 flex cursor-pointer text-center items-center text-lg whitespace-nowrap"
                                    onClick={() => setSettingsOpen(true)}
                                >
                                    <span className="text-[0.875rem] text-jtc-purple hover:text-jtc-blue">
                                        <span className="pb-[3px]">Settings</span>
                                    </span>
                                </a>
                            </div>
                            <div className="py-2 font-normal leading-7">
                                <a
                                    className="gap-1 w-fit ease-linear transition-transform duration-300 flex cursor-pointer text-center items-center text-lg whitespace-nowrap"
                                    onClick={() => setConfirmOpen(true)}
                                >
                                    <span className="text-[0.875rem] text-jtc-purple hover:text-jtc-blue">
                                        <span className="pb-[3px]">Log out</span>
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flyout-button flex justify-start cursor-pointer py-2 w-auto whitespace-nowrap items-center transition-all ease-linear duration-150 text-jtc-pink">
                    <img src={profileUrl} alt={userText} className="w-6 h-6 rounded-full object-cover mr-2" />
                    <a className="text-sm leading-6 uppercase tracking-[.15rem] font-bold after:content-['‹'] after:inline-block after:ml-2.5 after:rotate-90 ">
                        {userText}
                    </a>
                </div>
            </div>

            <SettingsDialog open={settingsOpen} setOpen={setSettingsOpen} />

            <LogoutConfirmDialog
                open={confirmOpen}
                email={email}
                onConfirm={() => { setConfirmOpen(false); logout(); }}
                onCancel={() => setConfirmOpen(false)}
            />
        </>
    );
};
